import type { Species, TraitKey } from '../../types';
import { t } from '../../strings';
import { studyList } from '../ignored';
import { newestBatch } from '../region';
import { isTraitValue, traitsOf, traitValueLabel, TRAIT_KEYS, TRAIT_LABEL } from '../traits';

// A focus: the birds one round is confined to, named in the URL that starts it.
//
// Every round the app has ever built asked about whatever FSRS handed over,
// which is right for review and wrong for the evening you want to sit down with
// the flycatchers. The engine never needed changing for this — `studySession`
// has always taken its pool as an argument and treated it as a filter — so all
// that was missing was somewhere to choose one. The Library is that somewhere:
// a filtered list is already a pool.
//
// A focus lasts exactly one round. It rides in the hash, which means a reload
// keeps it and leaving the route drops it, so it can never quietly narrow a
// round started later from the home screen. There is no focus state anywhere in
// this app; there is only the address of the round you are in.
//
// The codes are FROZEN into the token rather than re-derived at Start. The
// alternative — `lens=due`, resolved when the round is built — reads better in
// an address bar and is worse in every other way: it needs the card store, so
// resolving a focus becomes async and drags this whole module and its tests
// with it, and it lets the pool move between the button that promised nine
// birds and the round that asks about eight. The kind prefix is kept anyway,
// because a bare list of codes cannot tell the setup screen what it is looking
// at. So: `due:` names the focus, and the codes after it are the answer the
// Library already worked out.
//
// One thing this deliberately does NOT do is narrow the answer choices. A
// question's distractors keep coming from the whole pack (see `buildChoices`),
// because a twenty-question round drawing its wrong answers from nine birds
// becomes solvable by elimination — you learn which names have already been the
// answer, not which bird you heard — and a family round would be handing over
// the family for free.

/**
 * What a focus is, which is also what it is called on screen.
 *
 * `family` and `trait` resolve against the manifest, because family membership
 * and a bird's nest are facts about the birds rather than about the moment the
 * button was pressed. Every other kind carries its own codes; the kind is there
 * to name them.
 */
export type FocusKind =
  | 'family'
  | 'trait'
  | 'codes'
  | 'due'
  | 'weak'
  | 'missed'
  | 'unmet'
  | 'batch'
  | 'failed';

const KINDS: readonly FocusKind[] = [
  'family',
  'trait',
  'codes',
  'due',
  'weak',
  'missed',
  'unmet',
  'batch',
  'failed',
] as const;

export interface Focus {
  kind: FocusKind;
  /** Scientific family name — `kind: 'family'` only. */
  family?: string;
  /** One value on one axis, "all the cavity nesters" — `kind: 'trait'` only. */
  trait?: { axis: TraitKey; value: string };
  /** The birds themselves — every other kind. */
  codes?: readonly string[];
}

/**
 * Read a focus out of a route's `?focus=` parameter.
 *
 * Anything unrecognised reads as no focus rather than as an empty one, and the
 * round that follows is an ordinary round. A token this build has never heard
 * of is a stale link or a typo, and the app worked perfectly well before
 * focuses existed — the same tolerance the Library shows a saved chip it no
 * longer has.
 */
export function parseFocus(token: string | undefined): Focus | null {
  if (!token) return null;
  const at = token.indexOf(':');
  if (at < 0) return null;
  const kind = token.slice(0, at) as FocusKind;
  if (!KINDS.includes(kind)) return null;
  const rest = token.slice(at + 1);
  if (kind === 'family') return rest ? { kind, family: rest } : null;
  if (kind === 'trait') {
    // `nest=cavity`. A value the vocabulary no longer has is a stale link.
    const [axis, value] = rest.split('=') as [TraitKey, string | undefined];
    return TRAIT_KEYS.includes(axis) && value && isTraitValue(axis, value)
      ? { kind, trait: { axis, value } }
      : null;
  }
  const codes = rest.split(',').filter(Boolean);
  return codes.length ? { kind, codes } : null;
}

/** The token that carries `f` to a quiz route. */
export function focusToken(f: Focus): string {
  if (f.kind === 'family') return `family:${f.family}`;
  if (f.kind === 'trait') return `trait:${f.trait?.axis}=${f.trait?.value}`;
  return `${f.kind}:${(f.codes ?? []).join(',')}`;
}

/** Whether a bird is one of the birds a focus names, for the kinds that resolve. */
function holds(f: Focus, sp: Species): boolean {
  if (f.kind === 'family') return sp.family === f.family;
  if (f.kind === 'trait' && f.trait) return traitsOf(sp)[f.trait.axis].includes(f.trait.value);
  return false;
}

/**
 * The birds a focus actually comes to, in manifest order.
 *
 * Resolved against `studyList()` rather than `speciesList()`, which does four
 * jobs in one filter: a code from a pack that is no longer selected drops, a
 * code from a build that knew a species this one doesn't drops, a bird that has
 * been set aside drops, and the result comes back in the manifest's own order
 * however the token happened to list it.
 *
 * That third one is a rule worth saying out loud: setting a bird aside outranks
 * focusing on it. Setting aside is a decision about the bird, and a focus is a
 * fact about tonight.
 */
export function focusCodes(f: Focus): string[] {
  const from = studyList();
  if (f.kind === 'family' || f.kind === 'trait') {
    return from.filter((sp) => holds(f, sp)).map((sp) => sp.code);
  }
  if (f.kind === 'batch') {
    // The batch is re-derived rather than trusted, so a token from before the
    // last pack update names the batch that is newest now, not the one that was.
    const want = new Set(f.codes ?? []);
    return newestBatch(from)
      .filter((sp) => want.has(sp.code))
      .map((sp) => sp.code);
  }
  const want = new Set(f.codes ?? []);
  return from.filter((sp) => want.has(sp.code)).map((sp) => sp.code);
}

/**
 * What the focus is called above a setup screen, given how many birds this
 * mode can actually ask about.
 *
 * The count is the mode's, not the focus's, because they differ and the smaller
 * one is the honest number: nine sparrows is seven questions in Photo ID if two
 * of them have no photograph.
 */
export function focusLabel(f: Focus, n: number): string {
  switch (f.kind) {
    case 'family':
      return t('quiz.focus.family', { n, family: f.family ?? '' });
    case 'trait':
      return f.trait
        ? t('quiz.focus.trait', {
            n,
            axis: TRAIT_LABEL[f.trait.axis],
            value: traitValueLabel(f.trait.axis, f.trait.value),
          })
        : t('quiz.focus.library', { n });
    case 'due':
    case 'weak':
    case 'missed':
    case 'unmet':
    case 'batch':
    case 'failed':
      return t(`quiz.focus.${f.kind}`, { n });
    default:
      return t('quiz.focus.library', { n });
  }
}

/**
 * Narrow a mode's own pool to a focus.
 *
 * Two narrowings meet here and they mean different things. The pool is what
 * this skill CAN ask about — the birds with a recording, or with a photograph —
 * and the focus is what this round WILL ask about. The pool's order is kept,
 * because that is the order the queue understands.
 */
export function narrow(pool: string[], focus: readonly string[] | null | undefined): string[] {
  if (!focus) return pool;
  const want = new Set(focus);
  return pool.filter((c) => want.has(c));
}

/**
 * Why a focus came back empty, for the setup screen to say before it offers a
 * Start button it would have to take back.
 *
 * `null` means it did not: there are birds to study. The two failures are worth
 * telling apart because only one of them is the learner's own doing.
 */
export function emptyFocusReason(f: Focus, all: Species[]): string | null {
  if (focusCodes(f).length) return null;
  const known =
    f.kind === 'family' || f.kind === 'trait'
      ? all.some((sp) => holds(f, sp))
      : all.some((sp) => (f.codes ?? []).includes(sp.code));
  // A batch token that names birds the pack still has, but that are no longer
  // its newest, is a link kept across a pack update — not a decision the
  // learner made, and telling them they set these birds aside would be a lie.
  if (known && f.kind === 'batch') {
    const current = new Set(newestBatch(all).map((sp) => sp.code));
    if (!(f.codes ?? []).some((c) => current.has(c))) {
      return t('quiz.focus.emptyRelinked');
    }
  }
  return known
    ? t('quiz.focus.emptySetAside')
    : t('quiz.focus.emptyNotSelected');
}
